import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { makeErrorMessage } from "../makeErrorMessage";
import { addUser, removeUser } from "../redux/auth/slice";
import { useState, useEffect } from "react";

const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    console.error("Error decoding token", e);
    return null;
  }
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleAuthError = (error) => {
    if (error.response) {
      console.error("Server responded with an error:", error.response.data);
      makeErrorMessage(error.response.data || "Unknown error");
    } else if (error.request) {
      console.error("No response from the server:", error.request);
      makeErrorMessage("Server not responding. Try again later.");
    } else {
      console.error("Error during request setup:", error.message);
      makeErrorMessage("An unexpected error occurred.");
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = sessionStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available");

      const response = await axios.post(
          `/auth/refresh-token`,
          { refreshToken }
      );

      const newAccessToken = response.data.accessToken;
      sessionStorage.setItem("accessToken", newAccessToken);

      const decodedToken = decodeToken(newAccessToken);
      if (decodedToken) {
        dispatch(
            addUser({
              username: decodedToken.sub,
              role: decodedToken.role,
              token: newAccessToken,
            })
        );
      }

      return newAccessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      logOut();
      throw error;
    }
  };

  useEffect(() => {
    const checkTokenAndRefresh = async () => {
      const accessToken = sessionStorage.getItem("accessToken");
      const refreshToken = sessionStorage.getItem("refreshToken");

      if (!accessToken || !refreshToken) return;

      const decoded = decodeToken(accessToken);
      if (!decoded || !decoded.exp) return;

      const currentTime = Date.now() / 1000; // секунды
      const timeLeft = decoded.exp - currentTime;

      if (timeLeft < 120) {
        try {
          await refreshAccessToken();
          console.log("🔄 Access token refreshed automatically");
        } catch (err) {
          console.error("Failed to refresh access token:", err);
          logOut();
        }
      }
    };

    const interval = setInterval(checkTokenAndRefresh, 60000);
    return () => clearInterval(interval);
  }, []);

  const logIn = async (username, password) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
          `/auth/login`,
          { username, password }
      );

      const { accessToken, refreshToken } = response.data;
      sessionStorage.setItem("accessToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken);
      sessionStorage.setItem("username", username);

      const decodedToken = decodeToken(accessToken);
      if (decodedToken) {
        dispatch(
            addUser({
              username: decodedToken.sub,
              role: decodedToken.role,
              token: accessToken,
            })
        );
      }

      window.location.reload();
      Notify.success("Login successful");
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logOut = () => {
    try {
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("username");

      dispatch(removeUser());
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const checkIsUserLoggedIn = () => {
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      const username = sessionStorage.getItem("username");

      if (accessToken && username) {
        const decodedToken = decodeToken(accessToken);
        if (decodedToken) {
          dispatch(
              addUser({
                username: decodedToken.sub,
                role: decodedToken.role,
                token: accessToken,
              })
          );
        }
        return true;
      } else {
        dispatch(removeUser());
        return false;
      }
    } catch (error) {
      dispatch(removeUser());
      console.error("Error during user check:", error);
      return false;
    }
  };

  const signUp = async (formData) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
          `/auth/register/request`,
          formData
      );

      return { email: formData.email };
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async ({ email, username, password, otp }) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
          `/auth/register/verify`,
          { email, username, password, otp }
      );

      const { accessToken, refreshToken } = response.data;

      sessionStorage.setItem("accessToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken);
      sessionStorage.setItem("username", username);

      const decoded = decodeToken(accessToken);
      if (decoded) {
        dispatch(
            addUser({
              username: decoded.sub,
              role: decoded.role,
              token: accessToken,
            })
        );
      }

      Notify.success("Регистрация успешна");
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signUp,
    verifyOtp,
    logIn,
    logOut,
    checkIsUserLoggedIn,
    isLoading,
  };
};
